/**
 * StepHoleDiameter.jsx
 *
 * Wizard step 4 (Explore path): user picks a single hole diameter (mm).
 *
 * PORTED VERBATIM from TH ToolFinder v4:
 *   tophammer-toolfinder-v4/src/components/wizard/StepHoleDiameter.jsx
 *   (lines 40-372, version 4.7.2, Phase 4.7 — Feb 26, 2026)
 *
 * Visual elements (all ported faithfully):
 *   - Scaling circle (48-148px mapped to min-max diameter)
 *   - Bit count badge below the circle ("47 drill bits available")
 *   - "Most options" sandvik-gold badge on the popular diameter
 *   - Single-thumb range slider that snaps to nearest valid diameter
 *   - Tick marks for every valid diameter (clickable)
 *   - Quick-select pill grid (popular pill gets gold border + gold dot)
 *   - Skip button (clears holeDiameter and advances)
 *   - Empty state when no drill bits match the current config
 *   - Thread tag chips when effectiveThreads.length > 1
 *
 * MRT additions on top of the port:
 *   1. mm/inch toggle (top-right of header). The internal value stored
 *      in `selections.holeDiameter` is ALWAYS mm — unit is presentation
 *      only. Toggles label text on circle / ticks / pills / badge.
 *   2. WizardContext wiring instead of prop drilling — reads selections
 *      from useWizard(), pulls availableDiameters / bitCountsByDiameter
 *      from the diameterHistogram service.
 *
 * Wiring contract:
 *   - On select: handleSelect('holeDiameter', mm)
 *   - On Skip:   handleSelect('holeDiameter', null) → handleHoleDiameterNext()
 *   - NavigationButtons (in WizardLayout) drives Next/Previous via the
 *     shared handlers — but the in-step Skip button is owned by this
 *     component because the Skip semantics are local (clear + advance).
 *
 * Brand notes vs TF v4 source:
 *   - TF v4 raw hex (#1441F5, #A08C64, etc.) → Sandvik Tailwind tokens
 *     (electric-blue, sandvik-gold, n6, eb-1, eb-3, carbon-black).
 *   - No rounded corners (Sandvik convention; some TF v4 badges had
 *     subtle rounding — stripped).
 *
 * @module components/wizard/StepHoleDiameter
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D5 REVERT)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useWizard } from '@contexts/WizardContext';
import { formatInchesFraction } from '@utils/unitConversion';
import {
  getAvailableDiameters,
  getBitCountsByDiameter,
  ensureDiameterDataReady,
} from '@services/diameterHistogram';

/**
 * Format a millimetre value for display in the selected unit. mm is the
 * canonical stored unit — `unit === 'inch'` is presentation only.
 */
function formatDia(mm, unit) {
  if (mm == null || !Number.isFinite(mm)) return '—';
  if (unit === 'inch') return formatInchesFraction(mm);
  return `${mm}`;
}

function unitSuffix(unit) {
  return unit === 'inch' ? '' : 'mm';
}

export default function StepHoleDiameter() {
  const { selections, handleSelect, handleHoleDiameterNext } = useWizard();

  const productLine = selections.productLine === 'DTH' ? 'DTH' : 'TH';
  const selected = selections.holeDiameter; // number | null
  const holeDiameterMin = selections.holeDiameterMin;
  const holeDiameterMax = selections.holeDiameterMax;
  // Effective threads chain (single thread for now; ProductContext port in
  // Sub-Plan 3 will provide the multi-thread chain).
  const effectiveThreads = useMemo(
    () => (selections.threadSystem ? [selections.threadSystem] : null),
    [selections.threadSystem]
  );

  // UI state
  const [unit, setUnit] = useState('mm'); // 'mm' | 'inch' — local only
  const [hoveredPill, setHoveredPill] = useState(null);
  const [diametersReady, setDiametersReady] = useState(false);

  // Ensure product caches are loaded before reading per-diameter data.
  useEffect(() => {
    let cancelled = false;
    ensureDiameterDataReady().then(() => {
      if (!cancelled) setDiametersReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Pull diameter data (synchronous read once caches are warm).
  const availableDiameters = useMemo(() => {
    if (!diametersReady) return [];
    return getAvailableDiameters(productLine, effectiveThreads);
  }, [diametersReady, productLine, effectiveThreads]);

  const bitCountsByDiameter = useMemo(() => {
    if (!diametersReady) return {};
    return getBitCountsByDiameter(productLine, effectiveThreads);
  }, [diametersReady, productLine, effectiveThreads]);

  // Sorted [{mm, bits}] from TF v4.
  const sorted = useMemo(() => {
    return availableDiameters.map(mm => ({
      mm,
      bits: bitCountsByDiameter[mm] || 0,
    }));
  }, [availableDiameters, bitCountsByDiameter]);

  const minD = sorted.length > 0 ? sorted[0].mm : 0;
  const maxD = sorted.length > 0 ? sorted[sorted.length - 1].mm : 100;
  const currentData = sorted.find(d => d.mm === selected) || null;
  const hasConstraints = holeDiameterMin != null || holeDiameterMax != null;

  // Most popular (highest bit count) — TF v4 lines 69-71.
  const popular = sorted.length > 0
    ? sorted.reduce((a, b) => (b.bits > a.bits ? b : a))
    : null;

  // Snap slider to nearest valid diameter — TF v4 lines 74-88.
  const handleSlider = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    let closest = sorted[0]?.mm;
    let minDist = Infinity;
    for (const d of sorted) {
      const dist = Math.abs(d.mm - val);
      if (dist < minDist) {
        minDist = dist;
        closest = d.mm;
      }
    }
    if (closest != null) {
      handleSelect('holeDiameter', closest);
    }
  }, [sorted, handleSelect]);

  const handlePillClick = useCallback((mm) => {
    handleSelect('holeDiameter', mm);
  }, [handleSelect]);

  // Skip: clear diameter and advance (uses WizardContext handler).
  const handleSkip = useCallback(() => {
    handleSelect('holeDiameter', null);
    handleHoleDiameterNext();
  }, [handleSelect, handleHoleDiameterNext]);

  // Circle scale — TF v4 lines 102-104.
  const circleSize = selected
    ? 48 + ((selected - minD) / (maxD - minD || 1)) * 100
    : 80;

  // Empty state — TF v4 lines 107-134.
  if (diametersReady && sorted.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-2">
          No hole diameters available
        </h1>
        <p className="font-sandvik-text text-n6 mb-8">
          No drill bits match your current configuration.
          You can skip this step to see all available products.
        </p>
        <div className="flex justify-center">
          <button
            onClick={handleSkip}
            className="px-8 py-3 bg-electric-blue text-white font-sandvik-text font-normal hover:bg-eb-3 transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="hole-diameter-heading"
      className="max-w-2xl mx-auto px-4"
    >
      {/* =====================================================================
          HEADER  (TF v4 lines 138-151)  +  MRT mm/inch toggle (top-right)
          ===================================================================== */}
      <header className="mb-8 flex items-start justify-between gap-4">
        <div className="flex-1 text-center">
          <h1
            id="hole-diameter-heading"
            className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-2"
          >
            Select hole diameter
          </h1>
          <p className="font-sandvik-text text-n6">
            Drag the slider or tap a size below
          </p>
          {hasConstraints && (
            <p className="font-sandvik-text text-sm text-electric-blue mt-1">
              Filtered to {holeDiameterMin ?? '?'}–{holeDiameterMax ?? '?'} mm based on your equipment
            </p>
          )}
        </div>
        <div
          role="radiogroup"
          aria-label="Display units"
          className="flex shrink-0 gap-2"
        >
          {['mm', 'inch'].map((u) => (
            <button
              key={u}
              type="button"
              role="radio"
              aria-checked={unit === u}
              onClick={() => setUnit(u)}
              className={`font-sandvik-text text-xs px-3 py-1 border transition-colors ${
                unit === u
                  ? 'border-electric-blue text-electric-blue bg-eb-1'
                  : 'border-n3 text-n6 bg-white hover:border-n4'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </header>

      {/* =====================================================================
          CIRCLE VISUALIZATION  (TF v4 lines 153-228)
          Scales to represent physical hole size.
          ===================================================================== */}
      <div
        className="flex justify-center items-center"
        style={{ height: 200, marginBottom: 16 }}
      >
        <div className="flex flex-col items-center justify-center">
          {/* Drill hole circle */}
          <div
            className="flex flex-col items-center justify-center transition-all duration-300 ease-out"
            style={{
              width: circleSize,
              height: circleSize,
              borderRadius: '50%', // physical metaphor — circle keeps its round
              border: `3px solid ${selected ? '#1441F5' : '#DADADA'}`,
              background: selected
                ? 'radial-gradient(circle, rgba(20,65,249,0.04) 0%, rgba(20,65,249,0.10) 100%)'
                : '#F8F8F8',
              boxShadow: selected
                ? '0 0 0 8px rgba(20,65,249,0.06), 0 4px 24px rgba(20,65,249,0.12)'
                : 'none',
            }}
          >
            <span
              className="font-sandvik-headline font-normal leading-none transition-all duration-300"
              style={{
                fontSize: selected ? 32 : 24,
                color: selected ? '#1441F5' : '#9D9D9D',
              }}
            >
              {selected != null ? formatDia(selected, unit) : '—'}
            </span>
            <span
              className="font-sandvik-text transition-colors duration-300"
              style={{
                fontSize: 12,
                color: selected ? '#1441F5' : '#9D9D9D',
                marginTop: 2,
              }}
            >
              {unitSuffix(unit)}
            </span>
          </div>

          {/* Bit count badge — TF v4 lines 197-201 */}
          {currentData && (
            <div className="mt-3 px-4 py-1 bg-electric-blue text-white font-sandvik-text text-sm font-normal">
              {currentData.bits} drill bit{currentData.bits !== 1 ? 's' : ''} available
            </div>
          )}

          {/* "Most options" gold badge — TF v4 lines 204-208 */}
          {currentData && popular && selected === popular.mm && (
            <div className="mt-2 px-3 py-0.5 bg-sandvik-gold text-white font-sandvik-text text-xs font-normal tracking-wide">
              Most options
            </div>
          )}

          {/* Thread tag chips — TF v4 lines 211-226 */}
          {currentData && effectiveThreads && effectiveThreads.length > 1 && (
            <div className="mt-2 flex gap-1">
              {effectiveThreads.map(t => (
                <span
                  key={t}
                  className="px-2 py-0.5 text-xs font-sandvik-text font-normal border"
                  style={{
                    borderColor: 'rgba(20,65,249,0.3)',
                    color: '#1441F5',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* =====================================================================
          RANGE SLIDER  (TF v4 lines 230-247)
          Snaps to nearest valid diameter.
          ===================================================================== */}
      <div className="px-2 mb-2">
        <input
          type="range"
          min={minD}
          max={maxD}
          value={selected || Math.round((minD + maxD) / 2)}
          onChange={handleSlider}
          aria-label="Hole diameter slider (snaps to valid sizes)"
          className="w-full h-1.5 appearance-none cursor-pointer outline-none"
          style={{
            background:
              'linear-gradient(to right, #E6E6E6 0%, rgba(20,65,249,0.25) 50%, #E6E6E6 100%)',
            accentColor: '#1441F5',
            borderRadius: 0,
          }}
        />
      </div>

      {/* Tick marks — TF v4 lines 250-283 */}
      <div className="relative mx-2 mb-8" style={{ height: 44 }}>
        {sorted.map(d => {
          const pct = ((d.mm - minD) / (maxD - minD || 1)) * 100;
          const isActive = selected === d.mm;
          return (
            <button
              key={d.mm}
              type="button"
              onClick={() => handlePillClick(d.mm)}
              aria-label={`Select ${d.mm} mm`}
              className="absolute flex flex-col items-center bg-transparent border-none cursor-pointer p-0"
              style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
            >
              <div
                className="transition-all duration-200"
                style={{
                  width: 2,
                  height: isActive ? 16 : 10,
                  background: isActive ? '#1441F5' : '#DADADA',
                }}
              />
              <span
                className="font-sandvik-text whitespace-nowrap transition-all duration-200"
                style={{
                  fontSize: isActive ? 12 : 10,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? '#1441F5' : '#9D9D9D',
                  marginTop: 4,
                }}
              >
                {formatDia(d.mm, unit)}
              </span>
            </button>
          );
        })}
      </div>

      {/* =====================================================================
          QUICK SELECT GRID  (TF v4 lines 285-332)
          Compact pill buttons. Most-popular pill gets gold border + dot.
          ===================================================================== */}
      <div className="bg-n1 p-4 mb-6">
        <p className="font-sandvik-text text-xs font-normal text-n4 tracking-wider mb-3">
          Quick select
        </p>
        <div className="flex flex-wrap gap-2">
          {sorted.map(d => {
            const isSelected = selected === d.mm;
            const isPopular = popular && d.mm === popular.mm;
            const isHovered = hoveredPill === d.mm;
            return (
              <button
                key={d.mm}
                type="button"
                onClick={() => handlePillClick(d.mm)}
                onMouseEnter={() => setHoveredPill(d.mm)}
                onMouseLeave={() => setHoveredPill(null)}
                data-popular={isPopular ? 'true' : undefined}
                data-selected={isSelected ? 'true' : undefined}
                className="relative transition-all duration-150 font-sandvik-text"
                style={{
                  padding: '7px 14px',
                  fontSize: 13,
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? '#fff' : '#101010',
                  background: isSelected
                    ? '#1441F5'
                    : isHovered
                      ? '#E6E6E6'
                      : '#fff',
                  border: `1.5px solid ${
                    isSelected ? '#1441F5' : isPopular ? '#A08C64' : '#E6E6E6'
                  }`,
                  cursor: 'pointer',
                  borderRadius: 0,
                }}
              >
                {formatDia(d.mm, unit)}
                {unit === 'mm' ? 'mm' : ''}
                <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.6 }}>
                  ({d.bits})
                </span>
                {/* Popular dot — TF v4 lines 322-327 */}
                {isPopular && !isSelected && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-1 -right-1 w-2 h-2"
                    style={{ background: '#A08C64' }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* =====================================================================
          SKIP BUTTON
          Owned locally — clear holeDiameter + advance. Previous/Next come
          from the shared NavigationButtons in WizardLayout.
          ===================================================================== */}
      <div className="flex justify-center pt-2 mb-2">
        <button
          type="button"
          onClick={handleSkip}
          className="px-5 py-3 border-2 border-n3 font-sandvik-text font-normal text-n6 hover:border-n4 transition-colors"
        >
          Skip this step
        </button>
      </div>
    </section>
  );
}
